import City, { type GooglePlacesData } from '@/models/city'
import GooglePlacePhoto from '@/models/googlePlacePhoto'
import Recommendation from '@/models/recommendation'
import { getFirstPhotoUrl } from '@/utils/photoUtils'
import { debug } from '@/utils/logger'
import type { CountryPlaceCategory } from '@/types/country'

/** One "I've been here" visit per user and place (GET/PUT /api/v2/locations/:id/visit). */
export interface LocationVisit {
  rating: 'not_for_me' | 'liked' | 'loved' | null
  note?: string
  updated_at?: string
}

export default class Location {
  _id: string
  google_place_id?: string
  name: string
  city?: City
  label?: string
  country?: string
  category_name?: string
  google_rating?: number
  a3_intl_address?: string
  /** Google editorial summary (optional, ~80–150 chars) */
  description?: string
  /** Backend-written summary from the description + recent tips; written once, on first open. */
  ai_summary?: string
  /** Plain city name (Google locality, else UK postal town / borough); null when unknown. */
  city_name?: string | null
  /** Admin-set restaurant links (Google has none). */
  menu_url?: string
  opentable_rid?: string
  reservation_url?: string
  /** Signed-in user's "I've been here" visit (GET /api/v1/location/:id). */
  my_visit?: LocationVisit | null
  website?: string // Website URL extracted from data.website or google_places_data.websiteUri
  google_places_data?: Record<string, unknown>
  rec_count?: number
  /** Tags from the profile countries API: canonical country, US state, and place category. */
  country_name?: string | null
  state_name?: string | null
  categories?: CountryPlaceCategory[]
  // Breakdown of rec_count: { post_locations: N, explicit_recommendations: M }
  rec_count_breakdown?: {
    post_locations?: number
    explicit_recommendations?: number
  }
  geoposition?: {
    type: string
    coordinates: number[]
  }
  // Optional: avatars for recommendations, if backend provides them
  rec_references?: Array<{
    user_id?: string
    user_name?: string
    user_small_avatar_url?: string
    user_large_avatar_url?: string
  }>

  googlePlacesPrimaryTypeDisplayName?: string

  /** CDN or supplier hero (e.g. Nuitee) when Google photo identifiers are absent. */
  main_photo?: string

  photos: GooglePlacePhoto[] = [] // From Google Places data.

  recommendations: Recommendation[] = []
  sources: string[] = []

  get address(): string | undefined {
    const v = this.google_places_data?.formattedAddress
    return typeof v === 'string' ? v : undefined
  }

  get phone(): string | undefined {
    const v = this.google_places_data?.nationalPhoneNumber
    return typeof v === 'string' ? v : undefined
  }

  get websiteURI(): string | undefined {
    const v = this.google_places_data?.websiteUri
    return typeof v === 'string' ? v : undefined
  }

  get thumbnailPhotoURI(): string | undefined {
    // Use unified photo utility to extract photo URL from identifiers
    return getFirstPhotoUrl(
      {
        photos: this.photos,
        google_places_data: this.google_places_data,
      },
      400
    ) || undefined
  }

  get largePhotoURI(): string | undefined {
    // Use unified photo utility to extract photo URL from identifiers
    return getFirstPhotoUrl(
      {
        photos: this.photos,
        google_places_data: this.google_places_data,
      },
      1200
    ) || undefined
  }

  get city_identifying_name(): string | undefined {
    if (!this.google_places_data) return undefined
    return City.identifyingNameFromGooglePlacesData(
      this.google_places_data as unknown as GooglePlacesData
    )
  }

  private static numFromGoogleLatLng(
    v: number | { $numberDouble: string } | undefined
  ): number | undefined {
    if (typeof v === 'number' && Number.isFinite(v)) return v
    if (v && typeof v === 'object' && '$numberDouble' in v) {
      const n = Number(v.$numberDouble)
      return Number.isFinite(n) ? n : undefined
    }
    return undefined
  }

  get latitude(): number | undefined {
    if (this.geoposition?.coordinates?.length === 2) {
      return this.geoposition.coordinates[1]
    }
    const loc = this.google_places_data?.location as
      | GooglePlacesData['location']
      | undefined
    if (!loc) return undefined
    return Location.numFromGoogleLatLng(loc.latitude)
  }

  get longitude(): number | undefined {
    if (this.geoposition?.coordinates?.length === 2) {
      return this.geoposition.coordinates[0]
    }
    const loc = this.google_places_data?.location as
      | GooglePlacesData['location']
      | undefined
    if (!loc) return undefined
    return Location.numFromGoogleLatLng(loc.longitude)
  }

  constructor(data: Record<string, unknown>) {
    this._id = typeof data._id === 'string' ? data._id : String(data._id ?? '')
    this.google_place_id =
      typeof data.google_place_id === 'string' ? data.google_place_id : undefined
    this.name = typeof data.name === 'string' ? data.name : ''
    this.main_photo =
      typeof data.main_photo === 'string' && data.main_photo.trim() !== ''
        ? data.main_photo.trim()
        : undefined
    this.country = typeof data.country === 'string' ? data.country : ''
    this.country_name = typeof data.country_name === 'string' ? data.country_name : data.country_name === null ? null : undefined
    this.state_name = typeof data.state_name === 'string' ? data.state_name : data.state_name === null ? null : undefined
    this.categories = Array.isArray(data.categories)
      ? (data.categories.filter((category) => typeof category === 'string') as CountryPlaceCategory[])
      : undefined
    this.category_name =
      typeof data.category_name === 'string' ? data.category_name : ''
    this.google_rating =
      typeof data.google_rating === 'number' ? data.google_rating : undefined
    this.geoposition =
      data.geoposition && typeof data.geoposition === 'object'
        ? (data.geoposition as Location['geoposition'])
        : undefined
    this.a3_intl_address =
      typeof data.a3_intl_address === 'string' ? data.a3_intl_address : ''

    // Preserve Google editorial summary (description) from backend
    this.description = typeof data?.description === 'string' ? data.description : undefined
    this.ai_summary = typeof data?.ai_summary === 'string' ? data.ai_summary : undefined
    this.city_name = typeof data?.city_name === 'string' ? data.city_name : data?.city_name === null ? null : undefined
    this.menu_url = typeof data?.menu_url === 'string' ? data.menu_url : undefined
    this.opentable_rid =
      typeof data?.opentable_rid === 'string' || typeof data?.opentable_rid === 'number'
        ? String(data.opentable_rid)
        : undefined
    this.reservation_url = typeof data?.reservation_url === 'string' ? data.reservation_url : undefined
    this.my_visit =
      data?.my_visit && typeof data.my_visit === 'object'
        ? (data.my_visit as LocationVisit)
        : data?.my_visit === null
          ? null
          : undefined

    this.google_places_data =
      data.google_places_data && typeof data.google_places_data === 'object'
        ? (data.google_places_data as Record<string, unknown>)
        : undefined

    // Preserve recommendation counts/avatars from backend
    if (typeof data?.rec_count === 'number') {
      this.rec_count = data.rec_count
    } else if (typeof data?.recommendation_count === 'number') {
      // Fallback for legacy field names if present
      this.rec_count = data.recommendation_count
    }
    // Preserve rec_count_breakdown from backend (post_locations + explicit_recommendations)
    if (data?.rec_count_breakdown && typeof data.rec_count_breakdown === 'object') {
      this.rec_count_breakdown = data.rec_count_breakdown as Location['rec_count_breakdown']
    }
    const recRefsRaw = data?.rec_references ?? data?.recReferences
    if (Array.isArray(recRefsRaw)) {
      this.rec_references = recRefsRaw as Location['rec_references']
    }

    // Extract website: prioritize data.website (from backend), then google_places_data.websiteUri
    const gpdForWeb = data.google_places_data as Record<string, unknown> | undefined
    const gpdUri = gpdForWeb?.websiteUri
    this.website =
      (typeof data.website === 'string' ? data.website : undefined) ??
      (typeof gpdUri === 'string' ? gpdUri : undefined)

    // Handle photos: preserve identifiers (name, photo_reference) from backend
    // Backend sends identifiers only, so preserve them as-is
    // Priority: data.photos (if non-empty) > google_places_data.photos > empty array
    if (data?.photos && Array.isArray(data.photos) && data.photos.length > 0) {
      this.photos = data.photos.map(
        (p) => new GooglePlacePhoto(p as Record<string, unknown>)
      )
      debug('[Location] Constructor: Using data.photos', {
        name: this.name,
        photosCount: this.photos.length,
        firstPhotoHasName: !!(this.photos[0] as { name?: string })?.name,
        firstPhotoHasRef: !!(this.photos[0] as { photo_reference?: string })?.photo_reference,
      });
    } else if (
      this.google_places_data &&
      this.google_places_data.photos &&
      Array.isArray(this.google_places_data.photos) &&
      this.google_places_data.photos.length > 0
    ) {
      // Fallback: use google_places_data.photos (when data.photos is missing or empty)
      this.photos = (this.google_places_data.photos as unknown[]).map(
        (p) => new GooglePlacePhoto(p as Record<string, unknown>)
      )
      debug('[Location] Constructor: Using google_places_data.photos', {
        name: this.name,
        photosCount: this.photos.length,
        firstPhotoHasName: !!(this.photos[0] as { name?: string })?.name,
        firstPhotoHasRef: !!(this.photos[0] as { photo_reference?: string })?.photo_reference,
        dataPhotosWasEmpty: !!(data?.photos && Array.isArray(data.photos) && data.photos.length === 0),
      });
    } else {
      // No photos found
      this.photos = []
      debug('[Location] Constructor: No photos found', {
        name: this.name,
        hasDataPhotos: !!data?.photos,
        dataPhotosLength: Array.isArray(data?.photos) ? data.photos.length : 0,
        hasGooglePlacesData: !!this.google_places_data,
        hasGooglePlacesPhotos: !!this.google_places_data?.photos,
        googlePlacesPhotosLength: Array.isArray(this.google_places_data?.photos) ? this.google_places_data.photos.length : 0,
      });
    }

    const gpd = data.google_places_data as Record<string, unknown> | undefined
    const ptd = gpd?.primaryTypeDisplayName as { text?: unknown } | undefined
    const ptdText = ptd?.text
    this.googlePlacesPrimaryTypeDisplayName =
      typeof ptdText === 'string' ? ptdText : undefined

    this.recommendations =
      (data.recommendations as Record<string, unknown>[] | undefined)?.map(
        (recommendation) => new Recommendation(recommendation)
      ) ?? []

    this.sources = Array.isArray(data.sources)
      ? data.sources.filter((s): s is string => typeof s === 'string')
      : []
  }

  recommendedByAuthenticatedUser(userId: string | undefined): boolean {
    return this.recommendations.some(
      recommendation => recommendation.user_id === userId
    )
  }

  static removeDuplicateLocations(
    locationsWithDuplicates: Location[]
  ): Location[] {
    return locationsWithDuplicates.filter(
      (value, index, self) =>
        index ===
        self.findIndex(t => t.google_place_id === value.google_place_id)
    )
  }
}
